import React, { useContext } from "react";
import clsx from "clsx";

import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

import Chart from "./Chart";

// Icons for stat cards
const ChatIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const ClockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    primary: {
      bg: "bg-primary-50 dark:bg-primary-900/20",
      icon: "text-primary-500",
      border: "border-primary-200 dark:border-primary-800",
    },
    warning: {
      bg: "bg-warning-50 dark:bg-warning-900/20",
      icon: "text-warning-500",
      border: "border-warning-200 dark:border-warning-800",
    },
    success: {
      bg: "bg-success-50 dark:bg-success-900/20",
      icon: "text-success-500",
      border: "border-success-200 dark:border-success-800",
    },
  };

  const colors = colorClasses[color] || colorClasses.primary;

  return (
    <div
      className={clsx(
        "bg-white dark:bg-dark-800 rounded-xl border shadow-sm p-5 transition-all hover:shadow-md",
        colors.border
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-dark-500 dark:text-dark-400 mb-1">
            {title}
          </p>
          <p className="text-3xl font-bold text-dark-900 dark:text-white">
            {value}
          </p>
        </div>
        <div className={clsx("p-3 rounded-xl", colors.bg)}>
          <Icon className={clsx("w-6 h-6", colors.icon)} />
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  var userQueueIds = [];

  if (user.queues && user.queues.length > 0) {
    userQueueIds = user.queues.map((q) => q.id);
  }

  const GetTickets = (status, showAll, withUnreadMessages) => {
    const { count } = useTickets({
      status: status,
      showAll: showAll,
      withUnreadMessages: withUnreadMessages,
      queueIds: JSON.stringify(userQueueIds),
    });
    return count;
  };

  return (
    <div className="p-4 md:p-6 space-y-6 overflow-auto h-full">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <StatCard
          title={i18n.t("dashboard.messages.inAttendance.title")}
          value={GetTickets("open", "true", "false")}
          icon={ChatIcon}
          color="primary"
        />
        <StatCard
          title={i18n.t("dashboard.messages.waiting.title")}
          value={GetTickets("pending", "true", "false")}
          icon={ClockIcon}
          color="warning"
        />
        <StatCard
          title={i18n.t("dashboard.messages.closed.title")}
          value={GetTickets("closed", "true", "false")}
          icon={CheckCircleIcon}
          color="success"
        />
      </div>

      {/* Chart */}
      <div className="bg-white dark:bg-dark-800 rounded-xl border border-dark-200 dark:border-dark-700 shadow-sm p-5">
        <Chart />
      </div>
    </div>
  );
};

export default Dashboard;
